import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Menu, X, Shield, LogOut, Search, Square, Columns2, MessageSquare } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { ProfileDropdown } from '../ui/ProfileDropdown';
import { supabase } from '../../lib/supabase';
import { CreatePanel } from './CreatePanel';
import { fetchCreatePanelPreviews } from '../../lib/createPanel';

interface NavigationHeaderProps {
  currentView?: 'curations' | 'discover' | 'discussions' | 'contribute' | 'brands' | 'profile' | 'my-curations';
  onViewChange?: (view: 'curations' | 'discover' | 'discussions' | 'contribute' | 'brands' | 'profile' | 'my-curations') => void;
  user?: any;
  userProfile?: any;
  isAdmin?: boolean;
  onSearchClear?: () => void;
  showSearch?: boolean;
  onSearchToggle?: () => void;
  hasActiveFilters?: boolean;
}

export function NavigationHeader({
  currentView = 'discover',
  onViewChange,
  user,
  userProfile,
  isAdmin = false,
  onSearchClear,
  showSearch = false,
  onSearchToggle,
  hasActiveFilters = false
}: NavigationHeaderProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const createTriggerRef = useRef<HTMLButtonElement>(null);

  // Resolve panel previews before first open where possible.
  useEffect(() => {
    if (user) fetchCreatePanelPreviews().catch(() => {})
  }, [user]);

  // Close the panel on route change so it never lingers across pages.
  useEffect(() => { setIsCreateOpen(false) }, [location.pathname]);

  const closeCreate = (returnFocus: boolean) => {
    setIsCreateOpen(false);
    if (returnFocus) createTriggerRef.current?.focus();
  };

  const createUsername: string | null =
    userProfile?.username || (user?.email ? user.email.split('@')[0] : null);
  const [mobileColumns, setMobileColumns] = useState<1 | 2>(() => {
    // Load preference from localStorage
    const saved = localStorage.getItem('mobileColumnView');
    return saved === '2' ? 2 : 1;
  });

  // Check if we're viewing the current user's own profile
  const isViewingOwnProfile = () => {
    if (currentView !== 'profile') return false;

    const pathParts = location.pathname.split('/');
    const usernameInPath = pathParts[2]; // /profile/:username

    if (!usernameInPath || !user) return false;

    // Get current user's username
    const currentUsername = userProfile?.username || user.email?.split('@')[0];

    // Compare (case insensitive)
    return usernameInPath.toLowerCase() === currentUsername?.toLowerCase();
  };

  const handleViewChange = (view: 'curations' | 'discover' | 'discussions' | 'contribute' | 'brands' | 'profile' | 'my-curations') => {
    if (onViewChange) {
      onViewChange(view);
      onSearchClear?.();
    } else {
      // If no handler provided, navigate to home with the view
      navigate('/');
    }
    // Close mobile menu after navigation
    setIsMobileMenuOpen(false);
  };

  const handleLogout = async () => {
    await supabase.auth.signOut();
    navigate('/login');
    setIsMobileMenuOpen(false);
  };

  const handleColumnViewChange = (columns: 1 | 2) => {
    setMobileColumns(columns);
    localStorage.setItem('mobileColumnView', columns.toString());
    // Trigger a re-render by dispatching a custom event
    window.dispatchEvent(new CustomEvent('mobileColumnViewChange', { detail: columns }));
  };

  return (
    <>
      <header className="bg-[#F2F0E5] border-b sticky top-0 z-50">
        <div className="w-full px-4 lg:px-6">
          <div className="h-20 flex items-center relative">
            {/* Mobile Layout - Shows below 1024px */}
            <div className="flex lg:hidden items-center justify-between w-full">
              {/* Left-aligned Logo for Mobile */}
              <div className="flex-shrink-0" style={{ width: 'min(560px, 70vw)' }}>
                <img 
                  src="https://static.wixstatic.com/media/654322_f4aebad34f274603a614b55aed8193af~mv2.png/v1/fill/w_1360,h_232,al_c,q_85,usm_0.66_1.00_0.01,enc_avif,quality_auto/654322_f4aebad34f274603a614b55aed8193af~mv2.png"
                  alt="Purpose Collective"
                  className="w-full h-auto cursor-pointer object-contain"
                  style={{ maxHeight: '70px' }}
                  onClick={() => {
                    navigate('/');
                    setIsMobileMenuOpen(false);
                  }}
                />
              </div>
              
              {/* Mobile Right Side Buttons */}
              {user && (
                <div className="flex items-center gap-1">
                  {/* Search Button - Show in community and curated views */}
                  {(currentView === 'discover' || currentView === 'curations') && !location.pathname.startsWith('/product/') && !location.pathname.startsWith('/discussions') && (
                    <button
                      onClick={onSearchToggle}
                      className="p-2 hover:bg-gray-100 rounded-lg transition-colors relative"
                      aria-label="Search"
                    >
                      <Search className="h-5 w-5 text-gray-700" strokeWidth={2.5} />
                      {hasActiveFilters && (
                        <span className="absolute top-1 right-1 h-2 w-2 bg-black rounded-full" />
                      )}
                    </button>
                  )}

                  {/* Discussions Chat Icon */}
                  <button
                    onClick={() => {
                      handleViewChange('discussions');
                      setIsMobileMenuOpen(false);
                    }}
                    className={`p-2 rounded-lg transition-colors relative ${
                      currentView === 'discussions'
                        ? 'text-gray-900'
                        : 'text-gray-700 hover:bg-gray-100'
                    }`}
                    aria-label="Discussions"
                  >
                    <MessageSquare className="h-5 w-5" fill={currentView === 'discussions' ? 'currentColor' : 'none'} />
                  </button>

                  {/* Create trigger (opens the bottom sheet) */}
                  <button
                    data-create-trigger
                    onClick={() => { setIsCreateOpen(prev => !prev); setIsMobileMenuOpen(false); }}
                    aria-expanded={isCreateOpen}
                    aria-haspopup="menu"
                    aria-label="Create"
                    className="w-9 h-9 rounded-full bg-[#57241F] text-white flex items-center justify-center hover:bg-black transition-colors"
                  >
                    <svg width="14" height="14" viewBox="0 0 22 22" aria-hidden="true">
                      <path d="M11 4v14M4 11h14" stroke="#fff" strokeWidth="2.2" strokeLinecap="round" />
                    </svg>
                  </button>

                  {/* Hamburger Menu Button */}
                  <button
                    onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                    className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                    aria-label="Menu"
                  >
                    {isMobileMenuOpen ? (
                      <X className="h-6 w-6 text-gray-700" />
                    ) : (
                      <Menu className="h-6 w-6 text-gray-700" />
                    )}
                  </button>
                </div>
              )}
            </div>

            {/* Desktop Layout - Shows at 1024px and up */}
            <div className="hidden lg:flex items-center justify-between w-full">
              {/* Logo - Scales down on medium screens */}
              <div className="flex items-center">
                <img
                  src="https://static.wixstatic.com/media/654322_f4aebad34f274603a614b55aed8193af~mv2.png/v1/fill/w_1360,h_232,al_c,q_85,usm_0.66_1.00_0.01,enc_avif,quality_auto/654322_f4aebad34f274603a614b55aed8193af~mv2.png"
                  alt="Purpose Collective"
                  className="h-11 xl:h-12 2xl:h-14 w-auto cursor-pointer object-contain"
                  onClick={() => navigate('/')}
                />
              </div>
              
              {/* Centered Navigation - Progressive scaling for medium screens */}
              <nav className="flex absolute left-1/2 transform -translate-x-1/2 items-center">
                <button
                  onClick={() => handleViewChange('discover')}
                  data-track="nav-discover"
                  className={`px-4 xl:px-6 2xl:px-8 py-3 uppercase transition-all border-b-2 ${
                    currentView === 'discover'
                      ? 'text-gray-900 border-gray-900'
                      : 'text-gray-600 border-transparent hover:text-gray-900 hover:border-gray-300'
                  }`}
                  style={{ fontFamily: "'GT America Extended', 'Helvetica Neue', Helvetica, Arial, sans-serif", fontWeight: 700, letterSpacing: '0.02em', fontSize: '13.6px' }}
                >
                  Discover
                </button>
                <button
                  onClick={() => handleViewChange('curations')}
                  data-track="nav-curations"
                  className={`px-4 xl:px-6 2xl:px-8 py-3 uppercase transition-all border-b-2 ${
                    currentView === 'curations'
                      ? 'text-gray-900 border-gray-900'
                      : 'text-gray-600 border-transparent hover:text-gray-900 hover:border-gray-300'
                  }`}
                  style={{ fontFamily: "'GT America Extended', 'Helvetica Neue', Helvetica, Arial, sans-serif", fontWeight: 700, letterSpacing: '0.02em', fontSize: '13.6px' }}
                >
                  Curations
                </button>
                <button
                  onClick={() => {
                    if (userProfile?.username) {
                      navigate(`/profile/${userProfile.username}`);
                    } else if (user?.email) {
                      const username = user.email.split('@')[0];
                      navigate(`/profile/${username}`);
                    }
                  }}
                  data-track="nav-my-curations"
                  className={`px-4 xl:px-6 2xl:px-8 py-3 uppercase transition-all border-b-2 ${
                    isViewingOwnProfile()
                      ? 'text-gray-900 border-gray-900'
                      : 'text-gray-600 border-transparent hover:text-gray-900 hover:border-gray-300'
                  }`}
                  style={{ fontFamily: "'GT America Extended', 'Helvetica Neue', Helvetica, Arial, sans-serif", fontWeight: 700, letterSpacing: '0.02em', fontSize: '13.6px' }}
                >
                  My Curations
                </button>
                <button
                  onClick={() => handleViewChange('brands')}
                  data-track="nav-brands"
                  className={`px-4 xl:px-6 2xl:px-8 py-3 uppercase transition-all border-b-2 ${
                    currentView === 'brands'
                      ? 'text-gray-900 border-gray-900'
                      : 'text-gray-600 border-transparent hover:text-gray-900 hover:border-gray-300'
                  }`}
                  style={{ fontFamily: "'GT America Extended', 'Helvetica Neue', Helvetica, Arial, sans-serif", fontWeight: 700, letterSpacing: '0.02em', fontSize: '13.6px' }}
                >
                  Brands
                </button>
              </nav>
              
              {/* Right side actions - Scales down on medium screens */}
              {user && (
                <div className="flex items-center gap-1 xl:gap-2">
                  {/* Discussions Chat Icon */}
                  <button
                    onClick={() => handleViewChange('discussions')}
                    data-track="nav-discussions"
                    className={`p-2 rounded-lg transition-colors relative ${
                      currentView === 'discussions'
                        ? 'text-gray-900'
                        : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                    }`}
                    aria-label="Discussions"
                  >
                    <MessageSquare className="h-5 w-5" fill={currentView === 'discussions' ? 'currentColor' : 'none'} />
                  </button>

                  {/* Search Button - show on community, curated, and discussions views, not on product detail */}
                  {(currentView === 'discover' || currentView === 'curations' || currentView === 'discussions') && !location.pathname.startsWith('/product/') && (
                    <button
                      onClick={onSearchToggle}
                      className={`relative flex items-center gap-1.5 px-3 xl:px-4 2xl:px-6 py-3 text-base transition-all border-b-2 ${
                        showSearch
                          ? 'text-gray-900 border-gray-900'
                          : 'text-gray-600 border-transparent hover:text-gray-900 hover:border-gray-300'
                      }`}
                      style={{ fontFamily: 'Canela, Georgia, serif', letterSpacing: '-0.01em' }}
                      title="Toggle search"
                    >
                      <Search className="h-5 w-5" strokeWidth={2.5} />
                      <span style={{ fontFamily: "'GT America Extended', 'Helvetica Neue', Helvetica, Arial, sans-serif", fontWeight: 700, letterSpacing: '0.02em', fontSize: '13.6px' }}>SEARCH</span>
                      {hasActiveFilters && (
                        <span className="absolute -top-1 -right-1 h-2 w-2 bg-black rounded-full" />
                      )}
                    </button>
                  )}

                  {/* Create trigger — identical on every page, never relabeled */}
                  <button
                    ref={createTriggerRef}
                    data-create-trigger
                    onClick={() => setIsCreateOpen(prev => !prev)}
                    aria-expanded={isCreateOpen}
                    aria-haspopup="menu"
                    className="flex items-center gap-2 px-5 xl:px-6 2xl:px-8 py-2.5 uppercase transition-all rounded-full bg-[#57241F] text-white hover:bg-black shadow-md hover:shadow-lg whitespace-nowrap"
                    style={{
                      fontFamily: "'GT America Extended', 'Helvetica Neue', Helvetica, Arial, sans-serif",
                      fontWeight: 700,
                      letterSpacing: '0.02em',
                      fontSize: '13.6px',
                      boxShadow: isCreateOpen ? '0 0 0 3px rgba(87,36,31,.13)' : undefined,
                    }}
                  >
                    <svg width="14" height="14" viewBox="0 0 22 22" aria-hidden="true">
                      <path d="M11 4v14M4 11h14" stroke="#fff" strokeWidth="2.2" strokeLinecap="round" />
                    </svg>
                    Create
                  </button>

                  {/* Profile Dropdown */}
                  <div className="ml-1">
                    <ProfileDropdown
                      user={userProfile || user}
                      onProfileClick={() => handleViewChange('profile')}
                      isAdmin={isAdmin}
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
        {user && <CreatePanel open={isCreateOpen} username={createUsername} onClose={closeCreate} anchorRef={createTriggerRef} />}
      </header>

      {/* Mobile Menu Overlay */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="md:hidden fixed inset-0 z-[60] bg-black bg-opacity-50"
            onClick={() => setIsMobileMenuOpen(false)}
          >
            <motion.div
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: 'spring', damping: 25, stiffness: 300 }}
              className="absolute right-0 top-0 h-full w-80 bg-white shadow-xl flex flex-col"
              onClick={(e) => e.stopPropagation()}
            >
            {/* Menu Header - Close button only */}
            <div className="flex items-center justify-end p-4 border-b">
              <button
                onClick={() => setIsMobileMenuOpen(false)}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="h-5 w-5 text-gray-700" />
              </button>
            </div>

            {/* Menu Items */}
            <div className="flex flex-col flex-1 min-h-0">
              <div className="flex-1 overflow-y-auto p-6">
                {/* Main Navigation */}
                <nav className="space-y-1">
                  <button
                    onClick={() => handleViewChange('discover')}
                    data-track="nav-discover-mobile"
                    className={`w-full text-left px-4 py-3 rounded-lg uppercase transition-colors ${
                      currentView === 'discover'
                        ? 'bg-gray-100 text-gray-900 font-medium'
                        : 'text-gray-700 hover:bg-gray-50'
                    }`}
                    style={{ fontFamily: "'GT America Extended', 'Helvetica Neue', Helvetica, Arial, sans-serif", fontWeight: 700, letterSpacing: '0.02em', fontSize: '13.6px' }}
                  >
                    Discover
                  </button>
                  <button
                    onClick={() => handleViewChange('curations')}
                    data-track="nav-curations-mobile"
                    className={`w-full text-left px-4 py-3 rounded-lg uppercase transition-colors ${
                      currentView === 'curations'
                        ? 'bg-gray-100 text-gray-900 font-medium'
                        : 'text-gray-700 hover:bg-gray-50'
                    }`}
                    style={{ fontFamily: "'GT America Extended', 'Helvetica Neue', Helvetica, Arial, sans-serif", fontWeight: 700, letterSpacing: '0.02em', fontSize: '13.6px' }}
                  >
                    Curations
                  </button>
                  <button
                    onClick={() => {
                      if (userProfile?.username) {
                        navigate(`/profile/${userProfile.username}`);
                      } else if (user?.email) {
                        const username = user.email.split('@')[0];
                        navigate(`/profile/${username}`);
                      }
                      setIsMobileMenuOpen(false);
                    }}
                    data-track="nav-my-curations-mobile"
                    className={`w-full text-left px-4 py-3 rounded-lg uppercase transition-colors ${
                      isViewingOwnProfile()
                        ? 'bg-gray-100 text-gray-900 font-medium'
                        : 'text-gray-700 hover:bg-gray-50'
                    }`}
                    style={{ fontFamily: "'GT America Extended', 'Helvetica Neue', Helvetica, Arial, sans-serif", fontWeight: 700, letterSpacing: '0.02em', fontSize: '13.6px' }}
                  >
                    My Curations
                  </button>
                  <button
                    onClick={() => handleViewChange('brands')}
                    data-track="nav-brands-mobile"
                    className={`w-full text-left px-4 py-3 rounded-lg uppercase transition-colors ${
                      currentView === 'brands'
                        ? 'bg-gray-100 text-gray-900 font-medium'
                        : 'text-gray-700 hover:bg-gray-50'
                    }`}
                    style={{ fontFamily: "'GT America Extended', 'Helvetica Neue', Helvetica, Arial, sans-serif", fontWeight: 700, letterSpacing: '0.02em', fontSize: '13.6px' }}
                  >
                    Brands
                  </button>
                </nav>

                {/* Create — opens the same sheet as the header trigger */}
                <div className="my-6 border-t" />
                <button
                  data-create-trigger
                  onClick={() => { setIsMobileMenuOpen(false); setIsCreateOpen(true); }}
                  aria-haspopup="menu"
                  className="w-full px-8 py-3 rounded-full uppercase transition-all bg-[#57241F] text-white hover:bg-black shadow-md hover:shadow-lg flex items-center justify-center gap-2"
                  style={{ fontFamily: "'GT America Extended', 'Helvetica Neue', Helvetica, Arial, sans-serif", fontWeight: 700, letterSpacing: '0.02em', fontSize: '13.6px' }}
                >
                  <svg width="14" height="14" viewBox="0 0 22 22" aria-hidden="true">
                    <path d="M11 4v14M4 11h14" stroke="#fff" strokeWidth="2.2" strokeLinecap="round" />
                  </svg>
                  Create
                </button>
              </div>

              {/* Bottom Section with Admin, Logout, and Profile */}
              <div className="flex-shrink-0 border-t p-6">
                {/* Column View Toggle - Icons only */}
                <div className="flex gap-2 mb-4">
                  <button
                    onClick={() => handleColumnViewChange(2)}
                    className={`flex-1 py-3 rounded-lg transition-colors flex items-center justify-center ${
                      mobileColumns === 2
                        ? 'bg-gray-900 text-white'
                        : 'bg-white text-gray-700 border hover:bg-gray-100'
                    }`}
                    aria-label="Two column view"
                  >
                    <Columns2 className="h-5 w-5" />
                  </button>
                  <button
                    onClick={() => handleColumnViewChange(1)}
                    className={`flex-1 py-3 rounded-lg transition-colors flex items-center justify-center ${
                      mobileColumns === 1
                        ? 'bg-gray-900 text-white'
                        : 'bg-white text-gray-700 border hover:bg-gray-100'
                    }`}
                    aria-label="Single column view"
                  >
                    <Square className="h-5 w-5" />
                  </button>
                </div>

                {/* Bottom Actions */}
                <nav className="space-y-1 mb-4">
                  {isAdmin && (
                    <button
                      onClick={() => {
                        navigate('/admin');
                        setIsMobileMenuOpen(false);
                      }}
                      className="w-full text-left px-4 py-3 rounded-lg transition-colors flex items-center gap-3 text-gray-700 hover:bg-gray-50"
                    >
                      <Shield className="h-5 w-5" />
                      <span style={{ fontFamily: 'Canela, Georgia, serif' }}>Admin</span>
                    </button>
                  )}

                  <button
                    onClick={handleLogout}
                    className="w-full text-left px-4 py-3 rounded-lg transition-colors flex items-center gap-3 text-red-600 hover:bg-red-50"
                  >
                    <LogOut className="h-5 w-5" />
                    <span style={{ fontFamily: 'Canela, Georgia, serif' }}>Logout</span>
                  </button>
                </nav>

                {/* User Profile - At the very bottom */}
                {userProfile && (
                  <button
                    onClick={() => handleViewChange('profile')}
                    className={`w-full p-4 rounded-lg text-left transition-colors ${
                      currentView === 'profile'
                        ? 'bg-gray-900 text-white'
                        : 'bg-gray-50 hover:bg-gray-100'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      {userProfile.avatar_url ? (
                        <img
                          src={userProfile.avatar_url}
                          alt={userProfile.first_name || 'User'}
                          className="w-10 h-10 rounded-full object-cover"
                        />
                      ) : (
                        <div className={`w-10 h-10 rounded-full bg-gradient-to-br flex items-center justify-center font-semibold ${
                          currentView === 'profile' 
                            ? 'from-gray-600 to-gray-700 text-white' 
                            : 'from-purple-400 to-pink-400 text-white'
                        }`}>
                          {userProfile.first_name?.charAt(0) || userProfile.email?.charAt(0) || '?'}
                        </div>
                      )}
                      <div>
                        <p className={`font-medium ${
                          currentView === 'profile' ? 'text-white' : 'text-gray-900'
                        }`}>
                          {userProfile.first_name} {userProfile.last_name}
                        </p>
                        <p className={`text-sm ${
                          currentView === 'profile' ? 'text-gray-300' : 'text-gray-500'
                        }`}>View Profile</p>
                      </div>
                    </div>
                  </button>
                )}
              </div>
            </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      {/* End of Mobile Menu Overlay */}
    </>
  );
}